// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// The plunger, grabbed and pulled with a real hand (M5-08, XR-005).
//
// A small state machine — free, held, released — and a projection. Pure, and deliberately so:
// the skill shot is a *curve*, the curve is the only thing a player can practise, and a curve
// that only exists inside a WebXR frame callback is a curve nobody can test.
//
// **The pull is projected onto one axis.** A real plunger is a rod in a hole. A hand that grabs
// it and drifts sideways moves the hand, not the rod, so lateral movement is dropped and only
// the component along the plunger axis counts. Without this, a player pulling the knob toward
// their shoulder — which is what a hand naturally does — gets a plunge weaker than the distance
// their hand travelled, with no way to see why.
//
// **Grab is where the hand was, not where the knob is.** The pull is measured from the hand's
// position *at the moment of grabbing*, not from the knob's rest position. A player whose pinch
// registers 15 mm behind the knob would otherwise start the pull already 15 mm drawn, and the
// softest plunge available to them would be a medium one — which is precisely the range NEON
// NOVA's skill shot lives in.
//
// **Letting go slowly still fires.** 06 §2.2: "a real spring does not care how you let go." The
// release fires at the tension reached, and there is no minimum release speed, because the
// alternative punishes the exact careful hand a soft plunge needs.
//
// **A grab that never pulled fires nothing** (14 Sep 2026). Until then a grab-and-release
// launched at `PLUNGE_MIN` — "a hand that grabs and immediately lets go has still asked for the
// ball to be launched" — a rule written for a keyboard, where a key press is unambiguous. A hand
// is not: a pinch read within 45 mm of the knob and opened again is what a hand passing the knob
// looks like, and the product owner had watched the ball launch itself for four milestones: "the
// plunger seems to activate on its own at times (was always an issue). it should only send ball
// when user pulls plunger". So a release under `PLUNGE_DEADBAND` — the same 8 mm the buttons ask
// for — lets go of the knob and nothing else. The floor stays for a pull that crossed it.

import { PLUNGER_TRAVEL } from './cabinetControls'

/** What the plunger is doing. */
export type PlungerPhase = 'free' | 'held'

/** A fired plunge. */
export interface PlungeFired {
  /** 0 … 1, the value `PlayControls.launchPower` takes. */
  readonly power: number
  /** How far it was drawn, metres — for the sound and the animation. */
  readonly drawn: number
}

/**
 * The pull curve's exponent.
 *
 * 06 §2.2 asks for "a slight non-linearity at the top of the pull so a soft plunge is
 * controllable". `t^1.35` spends more of the travel in the low range: the first third of the
 * pull covers a quarter of the power instead of a third, which is where the super skill shot
 * lives. Sharper than about 1.6 and the top of the range becomes twitchy, which trades one
 * uncontrollable end for the other.
 */
export const PULL_EXPONENT = 1.35

/**
 * The weakest plunge a grab-and-release can produce.
 *
 * The same floor the keyboard has (`PLUNGE_MIN` in `input/desktop.ts`) and for the same reason:
 * a plunge of zero leaves the ball in the lane and reads as a control that did not work. A hand
 * that grabs and immediately lets go has still asked for the ball to be launched.
 */
export const PLUNGE_MIN = 0.15

/**
 * The least a plunger has to be drawn for letting go to fire it, metres.
 *
 * 8 mm: `PRESS_TRAVEL`, the same firm part a button asks for. Under it, a release is a hand
 * leaving the knob, not a plunge.
 */
export const PLUNGE_DEADBAND = 0.008

/** Power from a draw distance, metres. */
export function plungePower(drawn: number): number {
  if (!(drawn > 0)) return PLUNGE_MIN
  const t = drawn >= PLUNGER_TRAVEL ? 1 : drawn / PLUNGER_TRAVEL
  return PLUNGE_MIN + (1 - PLUNGE_MIN) * Math.pow(t, PULL_EXPONENT)
}

/** How many held frames back a release looks for the pull it fires at (1.0.245): about 0.2 s at 72 Hz. */
export const RELEASE_LOOKBACK_FRAMES = 14

export class PlungerGrab {
  private phase: PlungerPhase = 'free'
  /** Hand position along the axis when the grab began, metres. */
  private origin = 0
  private drawnNow = 0

  get held(): boolean {
    return this.phase === 'held'
  }

  /** How far back the knob is, metres — what the renderer draws and the sound pitches on. */
  get drawn(): number {
    return this.drawnNow
  }

  /** 0 … 1 spring tension, for the rising pitch cue and the controller haptic ramp. */
  get tension(): number {
    return this.drawnNow / PLUNGER_TRAVEL
  }

  /**
   * Begin a pull. `along` is the hand's position projected onto the plunger axis, metres.
   *
   * Returns false when a grab is already in progress, so a second hand closing on the knob does
   * not steal it mid-pull — which would otherwise reset the origin and drop the draw to zero.
   */
  grab(along: number): boolean {
    if (this.phase === 'held') return false
    this.phase = 'held'
    this.origin = along
    this.drawnNow = 0
    this.recent.length = 0
    return true
  }

  /**
   * The hand moved. `along` is again the projection onto the axis.
   *
   * Pushing *forward* past the rest position is clamped to zero rather than allowed to go
   * negative: a plunger cannot be pushed into the cabinet, and a negative draw would produce a
   * negative power on release.
   */
  move(along: number): void {
    if (this.phase !== 'held') return
    const pulled = along - this.origin
    this.drawnNow = pulled <= 0 ? 0 : pulled >= PLUNGER_TRAVEL ? PLUNGER_TRAVEL : pulled
  }

  /** The draws of the last held frames, for the release's look back (1.0.245). */
  private readonly recent: number[] = []

  /**
   * One held frame done: remember its draw. A release fires at the most it was drawn over the last
   * `RELEASE_LOOKBACK_FRAMES` (about 0.2 s at 72 Hz), because a hand moves forward as it opens and
   * the frames before the pinch reads open draw the rod back in with it — on the owner's 26 Oct
   * 08:01 log the rod stood at 55 mm with the pinch at 11 mm and 150 ms later let go "at 0 mm".
   * `PlungerGrab.cs` is the twin.
   */
  remember(): void {
    if (this.phase !== 'held') return
    this.recent.push(this.drawnNow)
    if (this.recent.length > RELEASE_LOOKBACK_FRAMES) this.recent.shift()
  }

  /** Let go. Returns the plunge, or undefined if nothing was held. */
  release(): PlungeFired | undefined {
    if (this.phase !== 'held') return undefined
    const drawn = Math.max(this.drawnNow, ...this.recent)
    this.recent.length = 0
    this.phase = 'free'
    this.drawnNow = 0
    if (drawn < PLUNGE_DEADBAND) return undefined
    return { power: plungePower(drawn), drawn }
  }

  /**
   * Keep the pull where it is across a change of frame (25 Sep 2026, the build-85 headset round).
   *
   * The hand is measured in the cabinet's frame, so a machine re-stood under a held rod — Meta's
   * recentre fires on a pinch held two seconds, which is exactly a pull — moved every along-axis
   * reading at once, and the rod jumped to the end of its travel and stayed there: *"the plunger
   * gets stuck near the end"*. The origin moves instead, so the drawn distance is unchanged and
   * the next movement of the hand is the next pull. `PlungerGrab.Rebase` is the twin.
   */
  rebase(along: number): void {
    if (this.phase !== 'held') return
    this.origin = along - this.drawnNow
  }

  /**
   * Drop the plunger **without firing it** (06 §2.5).
   *
   * The input-method switch case, and the only one: a player whose controller ran out of battery
   * mid-pull has not asked for a plunge, and firing one would launch the ball while they are
   * looking at their hand. A tracking dropout is the same event — the runtime stops reporting
   * the source, and this is what happens next.
   */
  cancel(): void {
    this.phase = 'free'
    this.drawnNow = 0
    this.recent.length = 0
  }
}

/**
 * Project a hand position onto the plunger axis, in metres from the cabinet origin.
 *
 * The axis is a unit vector, so this is a dot product; it is a named function because getting the
 * sign wrong produces a plunger that fires hardest when pushed, and a named thing can be tested.
 */
export function alongAxis(
  axis: { readonly x: number; readonly y: number; readonly z: number },
  x: number,
  y: number,
  z: number,
): number {
  return x * axis.x + y * axis.y + z * axis.z
}
